import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  FinancialVisualizationAccessibleColumn,
  FinancialVisualizationDescriptor,
  FinancialVisualizationSeriesDescriptor,
} from '@finance/contracts';
import { resolveTKey } from '../../i18n/resolve-key';
import { useLocalizedFormatter } from '../../runtime/formatting/use-localized-formatter';
import { SurfaceCard } from '../data-display/SurfaceCard';

export interface FinancialVisualizationRendererProps {
  descriptor: FinancialVisualizationDescriptor;
  rows: readonly Record<string, unknown>[];
  state?: string;
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function compareField(left: Record<string, unknown>, right: Record<string, unknown>, field: string): number {
  const leftNumber = finiteNumber(left[field]);
  const rightNumber = finiteNumber(right[field]);
  if (leftNumber !== null && rightNumber !== null) return leftNumber - rightNumber;
  return String(left[field] ?? '').localeCompare(String(right[field] ?? ''));
}

export function FinancialVisualizationRenderer({
  descriptor,
  rows,
  state = 'CURRENT',
}: FinancialVisualizationRendererProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { formatByType } = useLocalizedFormatter();

  const chartRows = useMemo(() => {
    const projected = rows
      .map((row) => ({
        ...row,
        __category: String(row[descriptor.categoryField] ?? '—'),
        ...Object.fromEntries(descriptor.series.map((series) => [
          `__series_${series.id}`,
          finiteNumber(row[series.field]),
        ])),
      }) as Record<string, unknown>)
      .filter((row) => descriptor.series.some((series) => row[`__series_${series.id}`] !== null));
    if (descriptor.sort) {
      projected.sort((left, right) => {
        const compared = compareField(left, right, descriptor.sort!.field);
        return descriptor.sort!.direction === 'DESC' ? -compared : compared;
      });
    }
    return projected.slice(0, descriptor.limit ?? projected.length);
  }, [descriptor, rows]);

  const formatSeries = (series: FinancialVisualizationSeriesDescriptor, value: unknown, row?: Record<string, unknown>) => {
    const precision = series.precision ?? 2;
    if (series.valueKind === 'PERCENT') {
      return formatByType(value, 'percentage', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    }
    if (series.valueKind === 'MONEY') {
      const currencyCode = series.currencySource === 'FIXED_CURRENCY'
        ? series.currencyCode
        : series.currencySource === 'ROW_CURRENCY' && series.currencyField
          ? String(row?.[series.currencyField] ?? '')
          : undefined;
      return formatByType(value, 'currency', {
        ...(currencyCode && /^[A-Z]{3}$/.test(currencyCode) ? { currencyCode } : {}),
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    }
    if (series.valueKind === 'QUANTITY') {
      return formatByType(value, 'quantity', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    }
    return formatByType(value, 'decimal', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  };

  const formatColumn = (column: FinancialVisualizationAccessibleColumn, row: Record<string, unknown>) => {
    const value = row[column.field];
    if (column.valueKind === 'TEXT') return value === null || value === undefined ? '—' : String(value);
    if (column.valueKind === 'DATE') return formatByType(value, 'date');
    return formatSeries({
      id: column.field,
      field: column.field,
      labelKey: column.labelKey,
      valueKind: column.valueKind,
      currencySource: column.currencySource ?? 'NONE',
      currencyField: column.currencyField,
      currencyCode: column.currencyCode,
      precision: column.precision,
    }, value, row);
  };

  if (rows.length === 0 || chartRows.length === 0) {
    return (
      <SurfaceCard surfaceRole="standard" padding="standard">
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          {t(resolveTKey(descriptor.titleKey))}
        </Typography>
        <Alert severity="info">{t(resolveTKey(descriptor.emptyStateKey))}</Alert>
      </SurfaceCard>
    );
  }

  const partial = state === 'PARTIAL';
  const primarySeries = descriptor.series[0];
  const primaryDataKey = `__series_${primarySeries.id}`;
  const horizontal = descriptor.type === 'HORIZONTAL_BAR';
  const line = descriptor.type === 'LINE';
  const chartHeight = Math.max(descriptor.minHeight ?? 260, horizontal ? chartRows.length * 38 + 72 : 260);

  return (
    <SurfaceCard surfaceRole="standard" padding="standard">
      <Typography variant="h6" component="h3">
        {t(resolveTKey(descriptor.titleKey))}
      </Typography>
      {descriptor.descriptionKey && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          {t(resolveTKey(descriptor.descriptionKey))}
        </Typography>
      )}
      {partial && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {t(resolveTKey(descriptor.partialStateKey))}
        </Alert>
      )}

      <Box role="figure" aria-label={t(resolveTKey(descriptor.titleKey))} sx={{ height: chartHeight, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          {line ? (
            <LineChart data={chartRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="__category" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
              <YAxis
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickFormatter={(value) => formatSeries(primarySeries, value)}
                width={80}
              />
              <RechartsTooltip
                formatter={(value) => formatSeries(primarySeries, value)}
                contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
              />
              <Line
                dataKey={primaryDataKey}
                name={t(resolveTKey(primarySeries.labelKey))}
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
                isAnimationActive={!reduceMotion}
              />
            </LineChart>
          ) : (
            <BarChart
              data={chartRows}
              layout={horizontal ? 'vertical' : 'horizontal'}
              margin={{ top: 8, right: 16, bottom: 8, left: horizontal ? 24 : 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              {horizontal ? (
                <>
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => formatSeries(primarySeries, value)}
                  />
                  <YAxis
                    type="category"
                    dataKey="__category"
                    width={120}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  />
                </>
              ) : (
                <>
                  <XAxis dataKey="__category" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                  <YAxis
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => formatSeries(primarySeries, value)}
                    width={80}
                  />
                </>
              )}
              <RechartsTooltip
                formatter={(value, _name, item) => formatSeries(
                  primarySeries,
                  value,
                  item.payload as Record<string, unknown>,
                )}
                contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
              />
              <Bar
                dataKey={primaryDataKey}
                name={t(resolveTKey(primarySeries.labelKey))}
                fill={theme.palette.primary.main}
                radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                isAnimationActive={!reduceMotion}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small" aria-label={t(resolveTKey(descriptor.accessibleTable.captionKey))}>
          <caption>{t(resolveTKey(descriptor.accessibleTable.captionKey))}</caption>
          <TableHead>
            <TableRow>
              {descriptor.accessibleTable.columns.map((column) => (
                <TableCell key={column.field}>{t(resolveTKey(column.labelKey))}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {chartRows.map((row, index) => (
              <TableRow key={String(row.id ?? `${descriptor.id}:${index}`)}>
                {descriptor.accessibleTable.columns.map((column) => (
                  <TableCell key={column.field}>{formatColumn(column, row)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </SurfaceCard>
  );
}
